import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Select, Tag, Space, Typography, Row, Col, message, Collapse, Badge, Avatar, Popconfirm } from 'antd';
import { TeamOutlined, YoutubeOutlined, EditOutlined, PlusOutlined, UserOutlined, LinkOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../utils/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

export default function StaffAssignment() {
  const [staffList, setStaffList] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignModal, setAssignModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedChannels, setSelectedChannels] = useState([]);

  useEffect(() => { fetchStaff(); fetchChannels(); }, []);

  const fetchStaff = async () => {
    setLoading(true);
    try { const res = await api.get('/staff/assignments'); setStaffList(res.data); } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchChannels = async () => {
    try { const res = await api.get('/channels'); setChannels(res.data); } catch (err) { console.error(err); }
  };

  const handleAssign = async () => {
    if (!selectedStaff) { message.error('Select a staff member'); return; }
    try {
      await api.post('/staff/assignments', { staff_id: selectedStaff, channel_ids: selectedChannels });
      message.success('Channels assigned successfully!');
      setAssignModal(false);
      setSelectedStaff(null);
      setSelectedChannels([]);
      fetchStaff();
    } catch (err) { message.error(err.response?.data?.error || 'Assignment failed'); }
  };

  const removeAssignment = async (assignmentId) => {
    try {
      await api.delete(`/staff/assignments/${assignmentId}`);
      message.success('Channel hata diya');
      fetchStaff();
    } catch (err) { message.error(err.response?.data?.error || 'Remove failed'); }
  };

  const openAssignModal = (staff) => {
    setSelectedStaff(staff.staff_id);
    setSelectedChannels(staff.channels.map(c => c.channel_id));
    setAssignModal(true);
  };

  const roleColors = {
    admin: '#f50', manager: '#2db7f5', viewer: '#87d068',
    graphic_designer: '#108ee9', video_editor: '#722ed1',
    distribution_staff: '#fa8c16', poster_viewer: '#13c2c2'
  };

  const roleLabels = {
    admin: 'Admin', manager: 'Manager', viewer: 'Viewer',
    graphic_designer: 'Graphic Designer', video_editor: 'Video Editor',
    distribution_staff: 'Distribution Staff', poster_viewer: 'Poster Viewer'
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}><TeamOutlined /> Staff Channel Assignment</Title>
          <Text type="secondary">Assign YouTube channels to staff members</Text>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setSelectedStaff(null); setSelectedChannels([]); setAssignModal(true); }}>
            Assign Channels
          </Button>
        </Col>
      </Row>

      {loading ? <Text>Loading...</Text> : (
        <Row gutter={[16, 16]}>
          {staffList.map(staff => (
            <Col xs={24} md={12} lg={8} key={staff.staff_id}>
              <Card
                title={
                  <Space>
                    <Avatar style={{ background: roleColors[staff.role] || '#667eea' }} icon={<UserOutlined />} />
                    <div>
                      <Text strong>{staff.full_name}</Text>
                      <br />
                      <Tag color={roleColors[staff.role]}>{roleLabels[staff.role] || staff.role}</Tag>
                    </div>
                  </Space>
                }
                extra={
                  <Button size="small" icon={<EditOutlined />} onClick={() => openAssignModal(staff)}>
                    Edit
                  </Button>
                }
                style={{ borderRadius: 12 }}
              >
                <div style={{ marginBottom: 8 }}>
                  <Badge count={staff.channel_count} style={{ backgroundColor: staff.channel_count > 0 ? '#52c41a' : '#d9d9d9' }}>
                    <Text strong><YoutubeOutlined style={{ color: '#ff0000' }} /> Channels Assigned</Text>
                  </Badge>
                </div>
                {staff.channels.length > 0 ? (
                  <div style={{ maxHeight: 260, overflow: 'auto' }}>
                    {staff.channels.map(ch => (
                      <div key={ch.channel_id} style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <YoutubeOutlined style={{ color: '#ff0000', marginRight: 6 }} />
                          <Text>{ch.channel_name}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>{ch.company_name}</Text>
                        </div>
                        <Space size={4}>
                          {ch.channel_url && (
                            <a href={ch.channel_url} target="_blank" rel="noreferrer"><LinkOutlined /></a>
                          )}
                          <Popconfirm
                            title="Ye channel is staff se hata dein?"
                            description="Sirf assignment hategi — channel aur uska data waisa hi rahega."
                            okText="Hata do"
                            cancelText="Nahi"
                            onConfirm={() => removeAssignment(ch.assignment_id)}
                          >
                            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </Space>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Text type="secondary">No channels assigned</Text>
                )}
              </Card>
            </Col>
          ))}
          {staffList.length === 0 && (
            <Col span={24}>
              <Card style={{ textAlign: 'center', padding: 40 }}>
                <TeamOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                <br /><br />
                <Text type="secondary">No staff members found. Create users first in User Management.</Text>
              </Card>
            </Col>
          )}
        </Row>
      )}

      <Modal
        title="Assign Channels to Staff"
        open={assignModal}
        onCancel={() => setAssignModal(false)}
        onOk={handleAssign}
        okText="Save Assignment"
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>Staff Member</Text>
            <Select
              value={selectedStaff}
              onChange={setSelectedStaff}
              placeholder="Select staff member"
              style={{ width: '100%', marginTop: 4 }}
            >
              {staffList.map(s => (
                <Option key={s.staff_id} value={s.staff_id}>
                  {s.full_name} ({roleLabels[s.role] || s.role})
                </Option>
              ))}
            </Select>
          </div>
          <div>
            <Text strong>Assign Channels (select multiple)</Text>
            <Select
              mode="multiple"
              value={selectedChannels}
              onChange={setSelectedChannels}
              placeholder="Select channels to assign"
              style={{ width: '100%', marginTop: 4 }}
              optionFilterProp="children"
            >
              {channels.map(ch => (
                <Option key={ch.id} value={ch.id}>
                  <YoutubeOutlined style={{ color: '#ff0000', marginRight: 4 }} />
                  {ch.channel_name} ({ch.company_name})
                </Option>
              ))}
            </Select>
          </div>
        </Space>
      </Modal>
    </div>
  );
}
